/**
 * ThermalOptions — optional add-on section rendered at the end of the
 * configurator wizard for sites ≥ 1 MW peak load. Collapsed by default.
 * Never required — leaving every field empty produces bit-identical engine
 * output vs pre-thermal behaviour (see backend iter43 invariance tests).
 *
 * The picker respects `enabled` from `/api/config → thermal_defaults`. Values
 * bubble up via `onChange(selection)` where selection follows the backend
 * `ThermalSelection` schema:
 *   {
 *     picks: [{tech, unit_mw, count, capacity_factor?}, …],
 *     chp: {enabled, host_tech},
 *     fuel_gas: {source, available, delivered_price_per_gj, ...},
 *     site_thermal: {heat_demand_kw, ...},
 *     ambient: {temp_c, altitude_m, derate_applied},
 *   }
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Info, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { FieldHelp } from "@/components/FieldHelp";
import { THERMAL_TECHS, GAS_SOURCES } from "@/lib/wizardOptions";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

export const ThermalOptions = ({ peakKw, onChange }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [picks, setPicks] = useState({});  // { gas_engine: {unit_mw, count}, ... }
  const [chp, setChp] = useState({ enabled: false, host_tech: "gas_engine" });
  const [fuelGas, setFuelGas] = useState({ source: "pipeline", available: true, delivered_price_per_gj: 8.0 });
  const [siteThermal, setSiteThermal] = useState({ heat_demand_kw: 0 });
  const [ambient, setAmbient] = useState({ temp_c: 35, altitude_m: 100, derate_applied: true });

  useEffect(() => {
    api.get("/config").then((r) => {
      const gen = r.data?.thermal_defaults?.thermal_generation || {};
      const overrides = r.data?.thermal_overrides?.thermal_generation || {};
      const merged = { ...gen };
      for (const k of Object.keys(overrides)) merged[k] = { ...gen[k], ...overrides[k] };
      setCfg({ ...r.data.thermal_defaults, thermal_generation: merged });
      setFuelGas((f) => ({ ...f, ...(r.data?.thermal_defaults?.fuel_gas || {}), ...(r.data?.thermal_overrides?.fuel_gas || {}) }));
      setAmbient((a) => ({ ...a, ...(r.data?.thermal_defaults?.ambient || {}) }));
    }).catch(() => {});
  }, []);

  // Peak-based eligibility hints so we grey out picks that will get rejected server-side anyway.
  const eligiblePicks = useMemo(() => {
    if (!cfg) return {};
    const enabled = new Set(cfg.thermal_generation.enabled || []);
    const out = {};
    for (const tech of ["gas_engine", "ocgt", "ccgt"]) {
      const spec = cfg.thermal_generation[tech];
      if (!spec || !enabled.has(tech)) { out[tech] = { ok: false, reasonKey: "wiz.th.disabled" }; continue; }
      const minMw = Number(spec.min_economic_mw || 0);
      const peakMw = Number(peakKw || 0) / 1000;
      out[tech] = peakMw + 1e-6 < minMw
        ? { ok: false, reasonKey: "wiz.th.below_min", peak: peakMw, min: minMw }
        : { ok: true };
    }
    return out;
  }, [cfg, peakKw]);
  const reasonText = (el) => t(el.reasonKey).replace("{{peak}}", num(el.peak, 1, 1)).replace("{{min}}", num(el.min, 0, 0));

  // Emit selection to parent whenever the user makes changes.
  useEffect(() => {
    const picksArr = Object.entries(picks)
      .filter(([, v]) => v && Number(v.count) > 0)
      .map(([tech, v]) => ({ tech, unit_mw: Number(v.unit_mw), count: Number(v.count) }));
    if (picksArr.length === 0 && !chp.enabled && !siteThermal.heat_demand_kw) {
      onChange && onChange(null);
      return;
    }
    onChange && onChange({
      picks: picksArr,
      chp: chp.enabled ? { enabled: true, host_tech: chp.host_tech } : { enabled: false },
      fuel_gas: fuelGas,
      site_thermal: siteThermal,
      ambient,
    });
  }, [picks, chp, fuelGas, siteThermal, ambient, onChange]);

  // Only surface the section on sites large enough for the smallest thermal tech (1 MW gas engine).
  if (Number(peakKw || 0) < 1000) return null;

  return (
    <section className="mt-8 border rounded-md" style={{ borderColor: "var(--hairline)", background: "#FBFAF7" }} data-testid="thermal-options">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3"
        data-testid="thermal-options-toggle"
      >
        <div className="flex items-center gap-2">
          <Flame size={14} style={{ color: "#873F00" }} />
          <span className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>{t("wiz.thermal.title")}</span>
          <span className="text-xs" style={{ color: "var(--muted)" }}>
            {t("wiz.thermal.sub")}
          </span>
        </div>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {open && cfg && (
        <div className="px-4 pb-5 pt-1 space-y-5">
          {/* Technology picks */}
          <div className="space-y-3">
            {["gas_engine", "ocgt", "ccgt"].map((tech) => {
              const spec = cfg.thermal_generation[tech];
              if (!spec) return null;
              const el = eligiblePicks[tech];
              const cur = picks[tech] || {};
              const [minMw, maxMw] = spec.unit_mw || [1, 10];
              const midMw = (minMw + maxMw) / 2;
              return (
                <div key={tech} className="rounded border p-3" style={{ borderColor: "var(--hairline)", background: "var(--white)", opacity: el?.ok ? 1 : 0.55 }} data-testid={`thermal-tech-${tech}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-1" style={{ color: "var(--charcoal)" }}>{t(THERMAL_TECHS[tech].title)}<FieldHelp k="help.thermal.units" label={t(THERMAL_TECHS[tech].title)} testid={`help-thermal-units-${tech}`} /></div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{t(THERMAL_TECHS[tech].body)}</div>
                    </div>
                    <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
                      <span>{t("wiz.th.count")}</span>
                      <input
                        type="number" min={0} max={30}
                        value={cur.count ?? 0}
                        disabled={!el?.ok}
                        onChange={(e) => setPicks((s) => ({ ...s, [tech]: { unit_mw: cur.unit_mw ?? midMw, count: Number(e.target.value) } }))}
                        className="w-14 px-2 py-1 border rounded text-sm"
                        style={{ borderColor: "var(--hairline)" }}
                        data-testid={`thermal-${tech}-count`}
                      />
                    </label>
                    <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
                      <span>{t("wiz.th.mw_unit")}</span>
                      <input
                        type="number" min={minMw} max={maxMw} step={0.5}
                        value={cur.unit_mw ?? midMw}
                        disabled={!el?.ok}
                        onChange={(e) => setPicks((s) => ({ ...s, [tech]: { unit_mw: Number(e.target.value), count: cur.count ?? 1 } }))}
                        className="w-20 px-2 py-1 border rounded text-sm"
                        style={{ borderColor: "var(--hairline)" }}
                        data-testid={`thermal-${tech}-size`}
                      />
                    </label>
                  </div>
                  {!el?.ok && el?.reasonKey && (
                    <div className="text-xs mt-2 flex items-center gap-1.5" style={{ color: "#873F00" }} data-testid={`thermal-reason-${tech}`}>
                      <Info size={11} /> {reasonText(el)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CHP */}
          <div className="rounded border p-3" style={{ borderColor: "var(--hairline)", background: "var(--white)" }}>
            <label className="text-sm flex items-center gap-2" data-testid="thermal-chp-row">
              <input
                type="checkbox"
                checked={chp.enabled}
                onChange={(e) => setChp((s) => ({ ...s, enabled: e.target.checked }))}
                data-testid="thermal-chp-toggle"
              />
              <span className="font-semibold" style={{ color: "var(--charcoal)" }}>{t("wiz.th.chp.title")}</span><FieldHelp k="help.thermal.chp" label={t("wiz.th.chp.title")} testid="help-thermal-chp" />
              <span className="text-xs" style={{ color: "var(--muted)" }}>{t("wiz.th.chp.sub")}</span>
            </label>
            {chp.enabled && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs" style={{ color: "var(--muted)" }}>
                  {t("wiz.th.chp.host")} <FieldHelp k="help.thermal.chp_host" label={t("wiz.th.chp.host")} testid="help-thermal-chp_host" />
                  <select
                    value={chp.host_tech}
                    onChange={(e) => setChp((s) => ({ ...s, host_tech: e.target.value }))}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm"
                    style={{ borderColor: "var(--hairline)" }}
                    data-testid="thermal-chp-host"
                  >
                    {(cfg.thermal_generation.chp?.applies_to || []).map((h) => (
                      <option key={h} value={h}>{THERMAL_TECHS[h] ? t(THERMAL_TECHS[h].title) : h}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs" style={{ color: "var(--muted)" }}>
                  {t("wiz.th.heat_demand")} <FieldHelp k="help.thermal.heat_demand" label={t("wiz.th.heat_demand")} testid="help-thermal-heat_demand" />
                  <input
                    type="number" min={0} step={100}
                    value={siteThermal.heat_demand_kw}
                    onChange={(e) => setSiteThermal((s) => ({ ...s, heat_demand_kw: Number(e.target.value) }))}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm"
                    style={{ borderColor: "var(--hairline)" }}
                    data-testid="thermal-heat-demand"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Gas fuel */}
          <div className="rounded border p-3" style={{ borderColor: "var(--hairline)", background: "var(--white)" }}>
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--charcoal)" }}>{t("wiz.th.gas_fuel")}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-xs" style={{ color: "var(--muted)" }}>
                {t("wiz.th.source")} <FieldHelp k="help.thermal.gas_source" label={t("wiz.th.source")} testid="help-thermal-gas_source" />
                <select
                  value={fuelGas.source}
                  onChange={(e) => setFuelGas((s) => ({ ...s, source: e.target.value }))}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  style={{ borderColor: "var(--hairline)" }}
                  data-testid="thermal-gas-source"
                >
                  {GAS_SOURCES.map((o) => <option key={o.id} value={o.id}>{t(o.key)}</option>)}
                </select>
              </label>
              <label className="text-xs flex items-center gap-2 self-end" style={{ color: "var(--muted)" }}>
                <input
                  type="checkbox"
                  checked={!!fuelGas.available}
                  onChange={(e) => setFuelGas((s) => ({ ...s, available: e.target.checked }))}
                  data-testid="thermal-gas-available"
                /> {t("wiz.th.gas_available")} <FieldHelp k="help.thermal.gas_available" label={t("wiz.th.gas_available")} testid="help-thermal-gas_available" />
              </label>
              <label className="text-xs" style={{ color: "var(--muted)" }}>
                {t("wiz.th.gas_price")} <FieldHelp k="help.thermal.gas_price" label={t("wiz.th.gas_price")} testid="help-thermal-gas_price" />
                <input
                  type="number" min={0} step={0.1}
                  value={fuelGas.delivered_price_per_gj}
                  onChange={(e) => setFuelGas((s) => ({ ...s, delivered_price_per_gj: Number(e.target.value) }))}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  style={{ borderColor: "var(--hairline)" }}
                  data-testid="thermal-gas-price"
                />
              </label>
            </div>
          </div>

          {/* Ambient overrides — pre-populated from the wizard's site inputs; user can nudge. */}
          <div className="rounded border p-3" style={{ borderColor: "var(--hairline)", background: "var(--white)" }}>
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--charcoal)" }}>{t("wiz.th.ambient.title")} <FieldHelp k="help.thermal.ambient" label={t("wiz.th.ambient.title")} testid="help-thermal-ambient" /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-xs" style={{ color: "var(--muted)" }}>
                {t("wiz.th.temp")}
                <input
                  type="number" min={0} max={60}
                  value={ambient.temp_c}
                  onChange={(e) => setAmbient((s) => ({ ...s, temp_c: Number(e.target.value) }))}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  style={{ borderColor: "var(--hairline)" }}
                  data-testid="thermal-ambient-temp"
                />
              </label>
              <label className="text-xs" style={{ color: "var(--muted)" }}>
                {t("wiz.th.altitude")}
                <input
                  type="number" min={0} max={5000} step={50}
                  value={ambient.altitude_m}
                  onChange={(e) => setAmbient((s) => ({ ...s, altitude_m: Number(e.target.value) }))}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  style={{ borderColor: "var(--hairline)" }}
                  data-testid="thermal-ambient-alt"
                />
              </label>
              <label className="text-xs flex items-center gap-2 self-end" style={{ color: "var(--muted)" }}>
                <input
                  type="checkbox"
                  checked={!!ambient.derate_applied}
                  onChange={(e) => setAmbient((s) => ({ ...s, derate_applied: e.target.checked }))}
                  data-testid="thermal-derate-applied"
                /> {t("wiz.th.derate")} <FieldHelp k="help.thermal.derate" label={t("wiz.th.derate")} testid="help-thermal-derate" />
              </label>
            </div>
          </div>

          <p className="text-xs" style={{ color: "var(--muted)" }}>
            {t("wiz.th.footnote")}
          </p>
        </div>
      )}
    </section>
  );
};

export default ThermalOptions;
